import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import LoadingScreen from './LoadingScreen';
import {
  FaCreditCard, FaShieldAlt, FaUniversity, FaCheck, FaTimes,
  FaExclamationTriangle, FaExternalLinkAlt, FaSpinner, FaBolt
} from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './StripeConnect.css';

const BASE_URL = 'https://api.rside.app';

function StripeConnect() {
  const navigate = useNavigate();
  const [loading, setLoading]         = useState(true);
  const [initials, setInitials]       = useState('');
  const [pmId, setPmId]               = useState(null);
  const [userEmail, setUserEmail]     = useState('');
  const [stripeStatus, setStripeStatus] = useState(null); // null = not connected, object = status data
  const [connecting, setConnecting]   = useState(false);
  const [error, setError]             = useState('');

  const fetchStripeStatus = useCallback(async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/Stripe/ConnectedAccountStatus?pm_id=${id}`);
      if (res.status === 404) { setStripeStatus(null); return; }
      if (res.ok) {
        const data = await res.json();
        setStripeStatus(data);
      }
    } catch {
      setStripeStatus(null);
    }
  }, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      try {
        const accountRes  = await fetch(`${BASE_URL}/MyAccount?Email=${encodeURIComponent(user.email)}`);
        const accountData = await accountRes.json();
        const parts = accountData.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));
        setUserEmail(user.email);
        const id = accountData.id;
        setPmId(id);
        if (id) await fetchStripeStatus(id);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate, fetchStripeStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    try {
      const isNotConnected = !stripeStatus;

      if (isNotConnected) {
        const createRes = await fetch(
          `${BASE_URL}/Stripe/CreateConnectedAccount?pm_id=${pmId}&email=${encodeURIComponent(userEmail)}`,
          { method: 'POST' }
        );
        if (!createRes.ok) throw new Error('Failed to create Stripe account.');
      }

      const linkRes = await fetch(`${BASE_URL}/Stripe/CreateOnboardingLink?pm_id=${pmId}`, { method: 'POST' });
      if (!linkRes.ok) throw new Error('Failed to generate onboarding link.');
      const linkData = await linkRes.json();
      const url = linkData?.url ?? linkData;

      if (typeof url === 'string' && url.startsWith('http')) {
        window.location.href = url;
      } else {
        throw new Error('Invalid onboarding URL received.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) return <LoadingScreen />;

  const isConnected = stripeStatus?.charges_enabled === true && stripeStatus?.payouts_enabled === true;
  const isPending   = stripeStatus && !isConnected;
  const isNotConnected = !stripeStatus;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Stripe Payments" />

        <div className="sc-body">

          <div id="sc-header" className="sc-page-header">
            <h2 className="sc-page-title">Stripe Payments</h2>
            <p className="sc-page-sub">Connect your account to accept rent payments directly from tenants.</p>
          </div>

          {/* ══ CONNECTED ══ */}
          {isConnected && (
            <>
              <div className="sc-connected-banner">
                <div className="sc-connected-check"><FaCheck /></div>
                <div className="sc-connected-text">
                  <p className="sc-connected-title">Stripe Connected</p>
                  <p className="sc-connected-sub">Your account is active — tenants can now pay rent through Stripe.</p>
                </div>
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noreferrer"
                  className="sc-btn sc-btn-outline"
                  style={{ width: 'auto', padding: '10px 20px', flexShrink: 0 }}
                >
                  Open Dashboard <FaExternalLinkAlt style={{ fontSize: 11, marginLeft: 6 }} />
                </a>
              </div>

              <div className="sc-content">
                <div id="sc-info-card" className="sc-info-card">
                  <p className="sc-info-card-title">Account status</p>
                  <div className="sc-badge-list">
                    <StatusBadge label="Charges Enabled"  value={stripeStatus?.charges_enabled} />
                    <StatusBadge label="Payouts Enabled"  value={stripeStatus?.payouts_enabled} />
                    <StatusBadge label="Details Submitted" value={stripeStatus?.details_submitted} />
                  </div>
                </div>

                <div id="sc-action-card" className="sc-action-card">
                  <div className="sc-icon-wrap green"><FaCheck /></div>
                  <p className="sc-action-title">Account Active</p>
                  <p className="sc-action-desc">Your Stripe account is fully set up and ready to accept payments.</p>
                  <div className="sc-pill sc-pill-green"><FaCheck style={{ fontSize: 10 }} /> Connected</div>
                  <a
                    href="https://dashboard.stripe.com"
                    target="_blank"
                    rel="noreferrer"
                    className="sc-btn"
                    style={{ marginTop: 8 }}
                  >
                    Open Stripe Dashboard <FaExternalLinkAlt style={{ fontSize: 11, marginLeft: 6 }} />
                  </a>
                </div>
              </div>
            </>
          )}

          {/* ══ PENDING ══ */}
          {isPending && (
            <div className="sc-content">
              <div id="sc-info-card" className="sc-info-card">
                <div className="sc-pending-header">
                  <div className="sc-icon-wrap amber" style={{ width: 52, height: 52, fontSize: 22, borderRadius: 14 }}>
                    <FaExclamationTriangle />
                  </div>
                  <div>
                    <p className="sc-info-card-title" style={{ marginBottom: 4 }}>Setup Incomplete</p>
                    <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
                      Your Stripe account was created but needs a few more details before you can accept payments.
                    </p>
                  </div>
                </div>

                <p className="sc-info-card-title" style={{ marginTop: 8 }}>Account status</p>
                <div className="sc-badge-list">
                  <StatusBadge label="Charges Enabled"   value={stripeStatus?.charges_enabled} />
                  <StatusBadge label="Payouts Enabled"   value={stripeStatus?.payouts_enabled} />
                  <StatusBadge label="Details Submitted" value={stripeStatus?.details_submitted} />
                </div>
              </div>

              <div id="sc-action-card" className="sc-action-card">
                <div className="sc-icon-wrap amber"><FaExclamationTriangle /></div>
                <p className="sc-action-title">Complete Your Setup</p>
                <p className="sc-action-desc">
                  Finish verifying your Stripe account to start accepting rent payments.
                </p>
                <div className="sc-pill sc-pill-amber">
                  <FaExclamationTriangle style={{ fontSize: 10 }} /> Setup Incomplete
                </div>
                {error && <p className="sc-error">{error}</p>}
                <button className="sc-btn" onClick={handleConnect} disabled={connecting}>
                  {connecting
                    ? <><FaSpinner className="sc-spin" /> Redirecting…</>
                    : 'Complete Setup'}
                </button>
              </div>
            </div>
          )}

          {/* ══ NOT CONNECTED ══ */}
          {isNotConnected && (
            <div className="sc-content">
              <div id="sc-info-card" className="sc-info-card">
                <p className="sc-info-card-title">Why connect Stripe?</p>
                <div className="sc-feature-list">
                  <div className="sc-feature-item">
                    <div className="sc-feature-icon"><FaUniversity /></div>
                    <div className="sc-feature-text">
                      <span className="sc-feature-title">Direct bank deposits</span>
                      <span className="sc-feature-desc">Tenant payments go straight to your linked bank account — no manual transfers needed.</span>
                    </div>
                  </div>
                  <div className="sc-feature-item">
                    <div className="sc-feature-icon green"><FaShieldAlt /></div>
                    <div className="sc-feature-text">
                      <span className="sc-feature-title">Secure &amp; PCI-compliant</span>
                      <span className="sc-feature-desc">Stripe handles all payment security so your tenants' card data is always protected.</span>
                    </div>
                  </div>
                  <div className="sc-feature-item">
                    <div className="sc-feature-icon purple"><FaBolt /></div>
                    <div className="sc-feature-text">
                      <span className="sc-feature-title">Fast payouts</span>
                      <span className="sc-feature-desc">Funds typically reach your bank within 2 business days after a tenant pays.</span>
                    </div>
                  </div>
                  <div className="sc-feature-item">
                    <div className="sc-feature-icon"><FaCreditCard /></div>
                    <div className="sc-feature-text">
                      <span className="sc-feature-title">Multiple payment methods</span>
                      <span className="sc-feature-desc">Tenants can pay using credit cards, debit cards, and bank transfers.</span>
                    </div>
                  </div>
                </div>
              </div>

              <div id="sc-action-card" className="sc-action-card">
                <div className="sc-icon-wrap"><FaCreditCard /></div>
                <p className="sc-action-title">Connect with Stripe</p>
                <p className="sc-action-desc">
                  Set up a Stripe account to receive tenant payments directly to your bank account.
                </p>
                <div className="sc-check-list">
                  <div className="sc-check-row"><div className="sc-check-dot"><FaCheck /></div>Receive rent payments directly</div>
                  <div className="sc-check-row"><div className="sc-check-dot"><FaCheck /></div>Secure, PCI-compliant processing</div>
                  <div className="sc-check-row"><div className="sc-check-dot"><FaCheck /></div>Fast payouts to your bank</div>
                </div>
                {error && <p className="sc-error">{error}</p>}
                <button className="sc-btn" onClick={handleConnect} disabled={connecting}>
                  {connecting
                    ? <><FaSpinner className="sc-spin" /> Connecting…</>
                    : 'Connect with Stripe'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function StatusBadge({ label, value }) {
  return (
    <div className="sc-status-badge">
      <div className={`sc-status-badge-icon ${value ? 'green' : 'gray'}`}>
        {value ? <FaCheck /> : <FaTimes />}
      </div>
      <span className="sc-status-badge-label">{label}</span>
      <span className={`sc-status-badge-val ${value ? 'green' : 'gray'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    </div>
  );
}

export default StripeConnect;
